import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import SocialLogin from '../../components/auth/SocialLogin';
import { useAuth } from '../../contexts/AuthContext';

const SignInPage = () => {
  const navigate = useNavigate();
  const { signIn, authError, loading, clearError } = useAuth();
  const [activeTab, setActiveTab] = useState('couple');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setFormData({ email: '', password: '', rememberMe: false });
    setValidationErrors({});
    if (authError) {
      clearError();
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: null }));
    }
    if (authError) {
      clearError();
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email?.trim()) newErrors.email = 'Email is required';
    if (!formData.password?.trim()) newErrors.password = 'Password is required';
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await signIn(formData.email, formData.password);
      
      if (result?.success) {
        // Navigation will be handled by AuthContext based on user role
      }
    } catch (error) {
      console.error('Sign in error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCulturalContent = () => {
    if (activeTab === 'couple') {
      return {
        title: 'Welcome Back, Couples!',
        subtitle: 'Continue planning your perfect South Asian wedding',
        heroText: 'Access your planning dashboard, saved vendors, and wedding checklist',
        bgImage: 'couple-signin-bg'
      };
    } else {
      return {
        title: 'Welcome Back, Vendors!',
        subtitle: 'Manage your business and connect with couples',
        heroText: 'Access your vendor dashboard, bookings, and client communications',
        bgImage: 'vendor-signin-bg'
      };
    }
  };

  const content = getCulturalContent();

  return (
    <div className="min-h-screen bg-surface-50">
      <Header />
      
      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-16">
        <div className="bg-white rounded-2xl shadow-soft border border-border overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Cultural Hero Section */}
            <div className="bg-gradient-to-br from-primary-50 to-secondary-50 p-8 lg:p-12 flex flex-col justify-center">
              <div className="max-w-md mx-auto lg:mx-0">
                <div className="flex items-center justify-center w-16 h-16 bg-primary rounded-full mb-6">
                  <Icon name={activeTab === 'couple' ? 'Heart' : 'Briefcase'} size={32} color="white" />
                </div>
                
                <h1 className="font-heading font-bold text-3xl text-text-primary mb-4">
                  {content.title}
                </h1>
                
                <p className="text-text-secondary font-caption text-lg mb-6">
                  {content.subtitle}
                </p>
                
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 mb-6">
                  <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                    What's Waiting for You
                  </h3>
                  <p className="text-text-secondary font-caption mb-4">
                    {content.heroText}
                  </p>
                  
                  <div className="flex flex-wrap gap-2">
                    {activeTab === 'couple' ? (
                      <>
                        <span className="px-3 py-1 bg-primary-100 text-primary text-xs rounded-full">Planning Tools</span>
                        <span className="px-3 py-1 bg-secondary-100 text-secondary text-xs rounded-full">Vendor Discovery</span>
                        <span className="px-3 py-1 bg-success-100 text-success text-xs rounded-full">Budget Tracker</span>
                      </>
                    ) : (
                      <>
                        <span className="px-3 py-1 bg-primary-100 text-primary text-xs rounded-full">Client Management</span>
                        <span className="px-3 py-1 bg-secondary-100 text-secondary text-xs rounded-full">Booking Calendar</span>
                        <span className="px-3 py-1 bg-success-100 text-success text-xs rounded-full">Invoice System</span>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="hidden lg:block">
                  <p className="text-sm text-text-muted font-caption">
                    "ShaadiNest made planning our {activeTab === 'couple' ? 'wedding' : 'business'} so much easier. 
                    The {activeTab === 'couple' ? 'vendor recommendations were perfect' : 'client management tools are amazing'}!"
                  </p>
                  <div className="flex items-center mt-3">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                      <Icon name="User" size={16} color="white" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-text-primary">
                        {activeTab === 'couple' ? 'Priya & Arjun' : 'Rajesh Photography'}
                      </p>
                      <p className="text-xs text-text-muted">
                        {activeTab === 'couple' ? 'Happy Couple' : 'Wedding Vendor'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sign In Form */}
            <div className="p-8 lg:p-12">
              <div className="max-w-md mx-auto lg:mx-0">
                <div className="mb-8">
                  <h2 className="font-heading font-bold text-2xl text-text-primary mb-2">
                    Sign In
                  </h2>
                  <p className="text-text-secondary font-caption">
                    Choose your account type and sign in to continue
                  </p>
                </div>

                {/* Tab Navigation */}
                <div className="flex bg-surface-100 rounded-xl p-1 mb-8">
                  <button
                    onClick={() => handleTabChange('couple')}
                    className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-lg transition-smooth ${
                      activeTab === 'couple' ?'bg-white text-primary shadow-soft' :'text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    <Icon name="Heart" size={18} color="currentColor" />
                    <span className="font-medium">Couple</span>
                  </button>
                  
                  <button
                    onClick={() => handleTabChange('vendor')}
                    className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-lg transition-smooth ${
                      activeTab === 'vendor'
                        ? 'bg-white text-primary shadow-soft' :'text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    <Icon name="Briefcase" size={18} color="currentColor" />
                    <span className="font-medium">Vendor</span>
                  </button>
                </div>

                {/* Social Login Section */}
                <SocialLogin 
                  orientation="vertical"
                  showDivider={false}
                  className="mb-6"
                />

                {/* Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-text-muted font-caption">
                      Or sign in with email
                    </span>
                  </div>
                </div>

                {/* Sign In Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Email Address
                    </label>
                    <Input
                      type="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={validationErrors.email ? 'border-error' : ''}
                      disabled={isSubmitting}
                    />
                    {validationErrors.email && (
                      <p className="text-error text-sm font-caption mt-1 flex items-center space-x-1">
                        <Icon name="AlertCircle" size={16} color="currentColor" />
                        <span>{validationErrors.email}</span>
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        className={validationErrors.password ? 'border-error pr-10' : 'pr-10'}
                        disabled={isSubmitting}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-text-muted hover:text-text-secondary"
                      >
                        <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} color="currentColor" />
                      </button>
                    </div>
                    {validationErrors.password && (
                      <p className="text-error text-sm font-caption mt-1 flex items-center space-x-1">
                        <Icon name="AlertCircle" size={16} color="currentColor" />
                        <span>{validationErrors.password}</span>
                      </p>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => handleInputChange('rememberMe', !formData.rememberMe)}
                        className={`flex items-center justify-center w-4 h-4 rounded border-2 transition-smooth ${
                          formData.rememberMe
                            ? 'bg-primary border-primary text-primary-foreground'
                            : 'border-border hover:border-primary'
                        }`}
                      >
                        {formData.rememberMe && (
                          <Icon name="Check" size={10} color="currentColor" />
                        )}
                      </button>
                      <span className="text-sm text-text-secondary">Remember me</span>
                    </label>
                    
                    <Link
                      to="/forgot-password"
                      className="text-sm text-primary hover:text-primary-600 transition-colors"
                    >
                      Forgot password?
                    </Link>
                  </div>

                  {authError && (
                    <div className="bg-error-50 border border-error-200 rounded-lg p-4">
                      <div className="flex items-center space-x-2">
                        <Icon name="AlertCircle" size={16} color="#DC2626" />
                        <span className="text-sm text-error font-caption">{authError}</span>
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    disabled={isSubmitting || loading}
                    loading={isSubmitting}
                    iconName="ArrowRight"
                    iconPosition="right"
                    iconSize={18}
                  >
                    {isSubmitting ? 'Signing In...' : 'Sign In'}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-text-secondary font-caption">
                    Don't have an account?{' '}
                    <Link
                      to="/sign-up-page"
                      className="text-primary hover:text-primary-600 font-medium transition-colors"
                    >
                      Sign up here
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Help & Support */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-soft border border-border p-6">
          <div className="flex items-start space-x-4">
            <div className="flex items-center justify-center w-10 h-10 bg-secondary-50 rounded-lg flex-shrink-0">
              <Icon name="HelpCircle" size={20} color="#DAA520" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-semibold text-text-primary mb-2">
                Need Help Signing In?
              </h3>
              <p className="text-text-secondary font-caption mb-4">
                Our support team is here to help you access your account
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="MessageCircle"
                  iconSize={16}
                >
                  Live Chat
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Mail"
                  iconSize={16}
                >
                  Email Support
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Phone"
                  iconSize={16}
                >
                  Call (555) 123-4567
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Note */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="text-center">
          <p className="text-sm text-text-muted font-caption">
            Trusted by thousands of couples and vendors across the South Asian wedding community
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SignInPage;